<script setup lang="ts">
import { useSimpleLoad } from '@/composables/useSimpleLoad';
import type { DialogServiceOptions } from '@/services/dialog.service';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';

const dialog = ref(false);
const options = ref<Partial<DialogServiceOptions>>({});

const { load: loadOnError, loading: loadingOnError } = useSimpleLoad();

async function showDialog(e: Event) {
  options.value = (e as CustomEvent).detail;
  await nextTick();
  dialog.value = true;
}

function closeDialog() {
  dialog.value = false;
  options.value = {};
}

function successButtonClick() {
  window.dispatchEvent(new CustomEvent('success-button-click', { detail: { successButtonClicked: true } }));
  closeDialog();
}

async function errorButtonClick() {
  let result = null;

  if (options.value.onError) {
    result = await loadOnError(options.value.onError)();
  }

  window.dispatchEvent(new CustomEvent('error-button-click', { detail: { errorButtonClicked: true, result } }));
  closeDialog();
}

onMounted(() => {
  window.addEventListener('show-dialog', showDialog);
  window.addEventListener('close-dialog', closeDialog);
});

onUnmounted(() => {
  window.removeEventListener('show-dialog', showDialog);
  window.removeEventListener('close-dialog', closeDialog);
});
</script>

<template>
  <v-dialog v-model="dialog" width="600px" :persistent="options.persistent">
    <v-card class="pa-4">
      <v-card-title> {{ options.title }} </v-card-title>

      <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
      <v-card-text class="text-grey-darken-1 px-4" v-html="options.description" />

      <v-card-actions :class="['d-flex', options.showErrorButton ? 'justify-space-between' : 'justify-end', 'mt-4']">
        <v-btn v-if="options.showErrorButton" color="grey-darken-1" :loading="loadingOnError" @click="errorButtonClick">
          {{ options.errorButtonText }}
        </v-btn>

        <v-btn color="success" @click="successButtonClick">
          {{ options.successButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
